"use client";

import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';
import { DataGrid, DataGridColumnHeader, TDataGridRequestParams, useDataGrid } from '../common/data-grid';
import { toast } from 'sonner';
import { useRouter } from "next/navigation";
import { downloadExtt, fetchExtensions, fetchMembershipExtensions } from "@/lib/action";
import { KeenIcon } from '../common/keenicons';
import Image from 'next/image';
import { extCategories, extStates } from './constant';
import { useSession, useUser } from '@clerk/nextjs';
import { ScreenLoader } from '../common/loaders/ScreenLoader';
import { Toolbar, ToolbarActions, ToolbarHeading } from '../common/toolbar';
import { DeleteConfirmation } from './DeleteConfirmation';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../common/ui/select';

const ExtensionLists = ({ ...props }) => {
    const { formatMessage } = useIntl();
    const [isLoading, setIsLoading] = useState<any>(false);
    const router = useRouter();
    const [isDeleteOpen, setIsDeleteOpen] = useState<any>(false);
    const [deleteI, setDeleteI] = useState<any>(0);
    const [name, setName] = useState("");
    const { user, isLoaded: userLoaded } = useUser();
    const { session, isLoaded: sessionLoaded } = useSession();
    const { permission, permissionLoading, membership, membershipLoading } = useSelector((state: any) => state.customer);
    const intl = useIntl();
    const [isRedirecting, setIsRedirecting] = useState(false);
    const isReady = user?.id && session?.id;
    const [memberships, setMemberships] = useState<any[]>([]);
    const [selectedMembership, setSelectedMembership] = useState<string>('0');


    useEffect(() => {
        if (isReady) {
            const membershipList = membership?.map((item: any) => ({
                id: item?.data?.id,
                title: `#${item?.data?.id} ${item?.data?.membership?.title ?? ''}`
            }));
            setMemberships(membershipList);
        }
    }, [isReady]);

    useEffect(() => {
        if (!permissionLoading && !membershipLoading && !isRedirecting && (permission?.extension == 0)) {
            toast.error(intl.formatMessage({ id: 'NO_PERMISSION_VIEW_PAGE' }));
            setIsRedirecting(true);
            router.push('/');
        }
    }, [permissionLoading, permission, membershipLoading, membership]);

    useEffect(() => {
        if (userLoaded && !user?.id) {
            router.push('/sign-in');
            toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
        }

        if (sessionLoaded && !session?.id) {
            router.push('/sign-in');
            toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
        }

    }, [userLoaded, sessionLoaded, user, session]);

    const deleteData = async (id: any, name: any) => {
        setDeleteI(id);
        setIsDeleteOpen(true);
        setName(name);
    }

    const handleClose = () => {
        setIsDeleteOpen(false);
        setDeleteI(null)
        setName("");
    };

    const fetchExtensionss = async (params: TDataGridRequestParams) => {
        try {
            const queryParams = new URLSearchParams();
            queryParams.set('searchJoin', 'or');
            queryParams.set('page', String(params.pageIndex + 1));
            queryParams.set('limit', String(params.pageSize));

            if (params.sorting?.[0]?.id) {
                queryParams.set('orderBy', params.sorting[0].id);
                queryParams.set('sortedBy', params.sorting[0].desc ? 'desc' : 'asc');
            }

            if (!user?.id) {
                toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
                return {
                    data: [],
                    totalCount: 0
                };
            }

            if (!session?.id) {
                toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
                return {
                    data: [],
                    totalCount: 0
                };
            }

            const membershipStr = typeof selectedMembership === "string" ? selectedMembership : String(selectedMembership);

            if (membershipStr.trim() !== "" && membershipStr !== "0") {
                queryParams.set("membership_id", membershipStr.trim());
            }


            let _res = props?.membership ? await fetchMembershipExtensions(user?.id, session?.id, queryParams.toString()) : await fetchExtensions({ page: queryParams.get('page'), pageSize: queryParams.get('limit') }, user?.id, session?.id);

            if (_res?.data?.error || _res?.error) {
                toast.error(_res?.data?.error ? _res?.data?.error : _res?.error  || intl.formatMessage({ id: 'SOMETHING_WENT_WRONG' }));

            } else {
                return {
                    data: _res?.data?.extensions ?? _res?.data?.data,
                    totalCount: props?.total ?? _res?.data?.total ?? _res?.data?.extensions.length
                };
            }

        } catch (error) {
            toast(formatMessage({ id: 'FAILED_TO_FETCH_EXTENSION_DATA' }));

            return {
                data: [],
                totalCount: 0
            };
        }

    };

    const downloadExtension = async (id: any) => {
        try {
            const _res = await downloadExtt(id, user?.id, session?.id);
            
            if (_res?.data?.message || _res?.message) {
                toast.error(_res?.data?.message || _res?.message  || intl.formatMessage({ id: 'SOMETHING_WENT_WRONG' }));
            } else if (_res?.data?.error || _res?.error) {
                toast.error(_res?.data?.message || _res?.message || _res?.data?.err || _res?.err || _res?.data?.error?.message || _res?.error || _res?.data?.msg || _res?.msg || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
            } else if (_res?.data?.link) {
                const url = `${_res.data.link}`;
                const link = document.createElement("a");
                link.href = url;
                link.download = _res.data.name || "download";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err: any) {
            toast.error(err?.message || intl.formatMessage({ id: 'MEMBERSHIP.ERROR' }));
        }
    };

    const columns = useMemo<ColumnDef<any>[]>(
        () => [
            {
                accessorFn: (row) => row.extname,
                id: 'extname',
                enableSorting: false,
                enableHiding: false,
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'EXTENSION_NAME', defaultMessage: 'Extension Name' })}
                        column={column}
                    />
                ),
                cell: (info) => (
                    <div className='flex items-center gap-2.5'>
                        <div className="shrink-0">
                            <Image
                                width={26}
                                height={26}
                                src={
                                    info.row.original.exticon?.trim()
                                        ? `${props?.baseurl}/storage/${info.row.original.exticon}`
                                        : '/assets/media/icon/add.png'
                                }
                                alt="Logo"
                                className="w-9 h-9 object-contain rounded-full shrink-0 bg-[#eee] b-0"
                            />
                        </div>
                        <div className='flex flex-col gap-1'>
                            <span className="leading-none font-medium text-sm text-gray-900">

                                {info.row.original.extname}
                            </span>
                            <span className='text-xs font-normal text-gray-700 leading-3'> {info.row.original.extkey}</span>
                        </div>
                    </div>
                ),
                meta: {
                    headerTitle: formatMessage({ id: 'EXTENSION_NAME', defaultMessage: 'Extension Name' })
                },
            },
            {
                accessorFn: (row) => row.vendorname,
                id: 'vendorname',
                enableSorting: false,
                enableHiding: false,
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'VENDOR', defaultMessage: 'Vendor' })}
                        column={column}
                    />
                ),
                cell: (info) => info.row.original.vendorname,
                meta: {
                    headerTitle: formatMessage({ id: 'VENDOR', defaultMessage: 'Vendor' }),
                    className: 'min-w-[135px]',
                },
            },
            {
                accessorFn: (row) => row.extstate,
                id: 'extstate',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'STATE', defaultMessage: 'State' })}
                        column={column}
                    />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => extStates?.map((_ttp: any, jj: any) => (
                    _ttp?.key == info.row.original?.extstate ?
                        <span key={"type" + jj + info.row.original?.id}> {_ttp?.val}</span>
                        : null
                )),
                meta: {
                    headerTitle: formatMessage({ id: 'STATE', defaultMessage: 'State' }),
                    className: 'min-w-[135px]',
                },
            },
            {
                accessorFn: (row) => row.extcat,
                id: 'extcat',
                enableSorting: false,
                enableHiding: false,
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'CATEGORY', defaultMessage: 'Category' })}
                        column={column}
                    />
                ),
                cell: (info) => extCategories?.map((_ttp: any, jj: any) => (
                    _ttp?.key == info.row.original?.extcat ?
                        <span key={"type" + jj + info.row.original?.id}> {_ttp?.val}</span>
                        : null
                )),
                meta: {
                    headerTitle: formatMessage({ id: 'CATEGORY', defaultMessage: 'Category' }),
                    className: 'min-w-[135px]',
                },
            },
            {
                accessorFn: (row) => row.extversion,
                id: 'extversion',
                enableSorting: false,
                enableHiding: false,
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'VERSION', defaultMessage: 'Version' })}
                        column={column}
                    />
                ),
                cell: (info) => (
                    <span className="flex flex-wrap gap-2.5 mb-2">
                        <span className="badge badge-sm badge-gray-200">
                            {info.row.original.extversion}
                        </span>
                    </span>
                ),
                meta: {
                    headerTitle: formatMessage({ id: 'VERSION', defaultMessage: 'Version' }),
                    className: 'min-w-[135px]',
                },
            },
            {
                accessorFn: (row) => row.status,
                id: 'status',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: "STATUS" })}
                        column={column} />
                ),
                enableSorting: false,
                enableHiding: false,
                cell: (info) => <span className={`badge badge-${info.row.original.status == 0 ? "info" : "warning"} shrink-0 badge-outline rounded-[30px]`}>
                    <span className={`size-1.5 rounded-full bg-${info.row.original.status == 0 ? "info" : "warning"} me-1.5`}></span>
                    {info.row.original.status == 0 ? <FormattedMessage id="DRAFT" /> : <FormattedMessage id="PUBLISH" />}
                </span>,
                meta: {
                    headerTitle: formatMessage({ id: "STATUS" }),
                    cellClassName: 'text-gray-800 font-normal'
                }
            },
            {
                id: 'download',
                header: () => '',
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    const currentRow = info.row.original;
                    const membershipId = currentRow?.membership;

                    const matchedMembership = membership?.find(
                        (item: any) => item?.data?.id === membershipId
                    );

                    const extensionConfig = matchedMembership?.data?.membership?.extension_config
                        ? JSON.parse(matchedMembership.data.membership.extension_config)
                        : {};

                    const downloadExt = extensionConfig?.download_ext;

                    const hasExtension = matchedMembership?.data?.membership?.extension;

                    const isDisabled = !hasExtension || !downloadExt

                    return (
                        <button
                            className="cursor-pointer btn btn-sm btn-icon btn-clear btn-light"
                            onClick={() => downloadExtension(info.row.original.id)}
                            disabled={isDisabled}
                        >

                            <KeenIcon icon="ki-filled ki-exit-down" />
                        </button>
                    )
                },
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },
            {
                id: 'edit',
                header: () => '',
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    const currentRow = info.row.original;
                    const membershipId = currentRow?.membership;

                    const matchedMembership = membership?.find(
                        (item: any) => item?.data?.id === membershipId
                    );

                    const extensionConfig = matchedMembership?.data?.membership?.extension_config
                        ? JSON.parse(matchedMembership.data.membership.extension_config)
                        : {};

                    const editExt = extensionConfig?.edit_ext;

                    const hasExtension = matchedMembership?.data?.membership?.extension;

                    const isExpired = matchedMembership?.data?.expired_at
                        ? new Date(matchedMembership.data.expired_at) < new Date()
                        : false;

                    const isDisabled = !hasExtension || !editExt || isExpired;

                    return (
                        <Link href={'/extensionbuilder/extension/' + info.row.original.id}
                            className={`${isDisabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'} btn btn-sm btn-icon btn-clear btn-light`}
                        >
                            <KeenIcon icon="ki-filled ki-notepad-edit" />
                        </Link >
                    )
                },
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },
            {
                id: 'remove',
                header: () => '',
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    const currentRow = info.row.original;
                    const membershipId = currentRow?.membership;
                    const matchedMembership = membership?.find(
                        (item: any) => item?.data?.id === membershipId
                    );
                    const isExpired = matchedMembership?.data?.expired_at
                        ? new Date(matchedMembership.data.expired_at) < new Date()
                        : false;
                    const isDisabled = isExpired;
                    return (
                        <button
                            className={`${isDisabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'} btn btn-sm btn-icon btn-clear btn-danger`}
                            onClick={() => deleteData(info.row.original.id, info.row.original.extname)}
                        >
                            <KeenIcon icon="ki-filled ki-trash" />
                        </button>
                    )
                },
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },
        ],
        [formatMessage, permission]
    );

    const Listbar = ({ }: any) => {
        const { table } = useDataGrid();
        const updateTable = () => {
            table.setColumnFilters(
                table.getState().columnFilters.filter((filter) => filter.id !== 'query')
            );
        }

        const filter = (value: any) => {
            if (typeof value == "string") {
                table.setPageIndex(0);
                if (value.trim() === '') {
                    table.setColumnFilters(
                        table.getState().columnFilters.filter((filter) => filter.id !== 'query')
                    );
                } else {
                    table.setColumnFilters([
                        ...table.getState().columnFilters.filter((filter) => filter.id !== 'query'),
                        { id: 'query', value: value },
                    ]);
                }
            } else {
                table.setColumnFilters(
                    table.getState().columnFilters.filter((filter) => filter.id !== 'query')
                );
            }
        };


        return (
            <>
                {
                    props?.membership ?
                        <div className="card-header border-b-0 px-5">
                            <h3 className="card-title"><FormattedMessage id="EXTENSIONS" /></h3>
                            <div className='flex flex-row gap-3 items-end justify-end '>
                                <div>
                                    <Select defaultValue={selectedMembership} onValueChange={(value) => {
                                        setSelectedMembership(value);
                                        filter(value);
                                    }}>
                                        <SelectTrigger className="min-w-32" size="sm">
                                            <SelectValue placeholder={formatMessage({ id: 'SELECT' })} />
                                        </SelectTrigger>
                                        <SelectContent className="min-w-32">
                                            <SelectItem value="0">
                                                <FormattedMessage id="ALL_MEMBERSHIPS" />
                                            </SelectItem>
                                            {memberships?.map((membership: any) => (
                                                <SelectItem key={membership.id} value={String(membership.id)}>
                                                    {membership.title}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div></div>
                        </div>
                        : null
                }
                {isDeleteOpen && <DeleteConfirmation open={isDeleteOpen} onOpenChange={handleClose} id={deleteI} updateTable={updateTable} name={name} userid={user?.id} sessionid={session?.id} />}
            </>
        );
    };

    if (permissionLoading || isRedirecting || membershipLoading) {
        return <ScreenLoader />;
    }

    if (!userLoaded && !sessionLoaded) {
        return (<ScreenLoader />)
    }

    if (user == undefined || user == null || session == undefined || session == null) {
        return (<ScreenLoader />)
    }

    if (isLoading) {
        return (<ScreenLoader />)
    }

    return (
        <Fragment>
            <div className={`${props?.membership ? "" : "container-fixed"}`}>
                {
                    props?.membership ?
                        ""
                        :
                        <Toolbar>
                            <ToolbarHeading title={<FormattedMessage id="EXTENSIONS" />} description="" />
                            <ToolbarActions>
                                <Link href={'/extensionbuilder/extension/'}
                                    className={`${permission?.extension == 0 || permission?.extension_info?.add == false ? "disabled" : "cursor-pointer"} btn btn-sm btn-primary`}>
                                    <i className="ki-filled ki-plus"></i> <FormattedMessage id="ADD_NEW_EXTENSION" />
                                </Link>
                            </ToolbarActions>
                        </Toolbar>
                }
            </div>
            <div className={`${props?.membership ? "" : "container-fixed"}`}>
                <DataGrid
                    columns={columns}
                    serverSide={true}
                    onFetchData={fetchExtensionss}
                    rowSelection={true}
                    getRowId={(row: any) => row.id}
                    pagination={{ size: props?.total ?? 10 }}
                    toolbar={<Listbar />}
                    layout={{
                        card: true,
                        classes: {
                            container: props?.total ? 'd-none-data-pagination' : ""
                        }
                    }}
                />
            </div>
        </Fragment>
    );
};

export default ExtensionLists;